import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  Paper,
  Chip,
  Stack,
  Avatar,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fade
} from '@mui/material';
import {
  TrendingUp as PropertyIcon,
  ShowChart as StockIcon,
  AccountBalance as BondIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon,
  Security as SecurityIcon,
  AccountBalanceWallet as WalletIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import StockSimulation from './stock/StockSimulation';
import { clearSimulationResult } from 'slices/fundManagementInvestment/simulationDashboardSlice';
import { AppDispatch } from 'app/store';
import PropSimulation from './property/PropertySimulation';
import BondSimulation from './Bond/BondSimulation';

type TabName = 'property' | 'stock' | 'bond';

interface TabPanelProps {
  children?: React.ReactNode;
  index: TabName;
  value: TabName;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simulation-tabpanel-${index}`}
      aria-labelledby={`simulation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ p: { xs: 1, sm: 2 } }}>{children}</Box>
        </Fade>
      )}
    </div>
  );
};

const SimulationDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedTab, setSelectedTab] = useState<TabName>('property');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabName) => {
    setSelectedTab(newValue);
    dispatch(clearSimulationResult());
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const tabConfig = [
    {
      value: 'property' as TabName,
      label: 'Property',
      fullLabel: 'Property Investment',
      description: '15-year real estate simulation with rental income analysis',
      timeframe: '15Y',
      color: '#81c784', // Light green
      bgColor: '#e8f5e8', // Very light green
      iconComponent: PropertyIcon,
      features: ['Rental Income', 'Capital Growth', 'Market Cycles']
    },
    {
      value: 'stock' as TabName,
      label: 'Stocks',
      fullLabel: 'Stock Investment',
      description: '12-year stock market simulation with dividend modeling',
      timeframe: '12Y',
      color: '#64b5f6', // Light blue
      bgColor: '#e3f2fd', // Very light blue
      iconComponent: StockIcon,
      features: ['Dividend Growth', 'Market Volatility', 'Compound Returns']
    },
    {
      value: 'bond' as TabName,
      label: 'Bonds',
      fullLabel: 'Bond Investment',
      description: '10-year bond simulation with stable fixed income',
      timeframe: '10Y',
      color: '#ffb74d', // Light orange
      bgColor: '#fff3e0', // Very light orange
      iconComponent: BondIcon,
      features: ['Fixed Income', 'Low Volatility', 'Capital Preservation']
    }
  ];

  const currentTab = tabConfig.find((tab) => tab.value === selectedTab);

  const MobileTabsDrawer = () => (
    <Drawer
      anchor="bottom"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '40vh'
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="600">
            Select Investment Type
          </Typography>
          <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Stack spacing={1}>
          {tabConfig.map((tab) => (
            <Box
              key={tab.value}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: selectedTab === tab.value ? tab.bgColor : 'grey.50',
                border:
                  selectedTab === tab.value ? `1px solid ${tab.color}` : '1px solid transparent',
                transition: 'all 0.2s ease'
              }}
              onClick={() => handleTabChange({} as any, tab.value)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: tab.color, width: 28, height: 28 }}>
                  <tab.iconComponent sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="600">
                    {tab.fullLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tab.description}
                  </Typography>
                </Box>
                <Chip
                  label={tab.timeframe}
                  size="small"
                  sx={{ bgcolor: tab.color, color: 'white' }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );

  if (!currentTab) {
    return <div>Loading...</div>;
  }

  return (
    <Container maxWidth="xl" sx={{ p: { xs: 0, sm: 2 } }}>
      {/* Compact Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 1.5, sm: 2 } ,p:0}}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight="700"
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem' },
            color: 'primary.main',
            mb: 0.5
          }}
        >
          Investment Simulation Dashboard
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: { xs: 1, sm: 1.5 },
            fontSize: { xs: '0.8rem', sm: '0.9rem' }
          }}
        >
          Monte Carlo Analysis for Smart Investment Decisions
        </Typography>
      </Box>

      {/* Mobile Navigation Bar */}
      {isMobile && (
        <Paper
          elevation={1}
          sx={{
            mb: 1.5,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.50',
            p: 0.5, 
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              cursor: 'pointer',
              bgcolor: currentTab.bgColor
            }}
            onClick={() => setMobileDrawerOpen(true)}
          >
            <Avatar sx={{ bgcolor: currentTab.color, mr: 1.5, width: 32, height: 32 }}>
              <currentTab.iconComponent sx={{ fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="600" color={currentTab.color}>
                {currentTab.fullLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tap to switch • {currentTab.timeframe}
              </Typography>
            </Box>
            <MenuIcon color="action" fontSize="small" />
          </Box>
        </Paper>
      )}

      {/* Desktop Navigation Tabs - Single Navigation */}
      {!isMobile && (
        <Paper
          elevation={1}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.50'
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 60,
              '& .MuiTab-root': {
                minHeight: 60,
                fontSize: '0.9rem',
                fontWeight: 600,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                color: 'text.secondary'
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            {tabConfig.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: tab.bgColor,
                    color: tab.color
                  }
                }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: selectedTab === tab.value ? tab.color : 'grey.300',
                        width: 28,
                        height: 28,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <tab.iconComponent sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography
                        variant="body2"
                        component="div"
                        fontWeight="600"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {tab.fullLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {tab.timeframe} • Monte Carlo
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Compact Tab Info Banner */}
      <Fade in={true} timeout={300}>
        <Paper
          elevation={1}
          sx={{
            p: { xs: 0.5, sm: 2 },
            mb: 1.5,
            borderRadius: 2,
            bgcolor: currentTab.bgColor,
            border: `1px solid ${currentTab.color}30`
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
              <Avatar
                sx={{
                  bgcolor: currentTab.color,
                  width: 36,
                  height: 36
                }}
              >
                <currentTab.iconComponent sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="600"
                  color={currentTab.color}
                  sx={{ lineHeight: 1.2 }}
                >
                  {currentTab.fullLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                  {currentTab.description}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Tooltip title="Monte Carlo Iterations" arrow>
                <Chip
                  icon={<AssessmentIcon sx={{ fontSize: '14px !important' }} />}
                  label="10K"
                  variant="outlined"
                  size="small"
                  sx={{ bgcolor: 'white', borderColor: currentTab.color }}
                />
              </Tooltip>
              <Chip
                label={currentTab.timeframe}
                size="small"
                sx={{
                  bgcolor: currentTab.color,
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {/* Tab Content with Reduced Spacing */}
      <Box
        sx={{
          minHeight: { xs: '65vh', sm: '75vh' },
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${currentTab.color}20`,
          bgcolor: 'white',
          p: 0
        }}
      >
        <TabPanel value={selectedTab} index="property">
          <PropSimulation />
        </TabPanel>

        <TabPanel value={selectedTab} index="stock">
          <StockSimulation />
        </TabPanel>

        <TabPanel value={selectedTab} index="bond">
          <BondSimulation />
        </TabPanel>
      </Box>

      {/* Compact Footer */}
      <Paper
        elevation={1}
        sx={{
          mt: 2,
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 2
        }}
      >
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle2"
            fontWeight="600"
            textAlign="center"
            color="primary.main"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <InsightsIcon fontSize="small" />
            Investment Analysis Features
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-around">
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <TimelineIcon color="primary" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Monte Carlo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                10K iterations for accuracy
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <SecurityIcon color="primary" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Risk Analysis
              </Typography>
              <Typography variant="caption" color="text.secondary">
                P10, P50, P90 scenarios
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <WalletIcon color="primary" sx={{ fontSize: 24, mb: 0.5 }} />
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Strategic Insights
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Actionable recommendations
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Mobile Tabs Drawer */}
      <MobileTabsDrawer />
    </Container>
  );
};

export default SimulationDashboard;
